/**
 * Blades clip-path: corner curvature stays constant in pixels regardless of
 * block size. Clip is applied via CSS path() in pixel space.
 *
 * Important (Firefox 154+): do NOT combine clip-path with CSS transforms on
 * these layers — WebRender leaves colored "ghost" bands stuck to the viewport
 * while scrolling. Direction / offsets are handled via path flipping and
 * top/bottom positioning instead of scaleY/translateY.
 */
let tangentHeightCSSVarInitialized = false

function bladesDynamicClipPath() {
  const CORNER_SIZE_PX = 20
  const BREAKPOINT_MD_PX = 768

  const CORNER_SIZE_RATIO = () => (window.innerWidth >= BREAKPOINT_MD_PX ? 0.071 : 0.1)

  const REF = CORNER_SIZE_PX / CORNER_SIZE_RATIO()
  const LEFT_Y_BASE = 0.058

  const getReferenceHeight = () => (window.innerWidth >= BREAKPOINT_MD_PX ? 550 : 200)

  const getTangentHeight = () => LEFT_Y_BASE * getReferenceHeight()

  if (!tangentHeightCSSVarInitialized) {
    tangentHeightCSSVarInitialized = true
    const setTangentHeightCSSVar = () => {
      document.documentElement.style.setProperty(
        '--blades-tangent-height',
        `${getTangentHeight()}px`,
      )
    }
    setTangentHeightCSSVar()
    window.addEventListener('resize', setTangentHeightCSSVar)
  }

  function computePathParams(width, height) {
    if (width <= 0 || height <= 0) return null

    const originalInTangentPxY = LEFT_Y_BASE * getReferenceHeight()
    const scaleX = Math.min((REF / width) * 4, 1)
    const scaleY = Math.min(REF / height, 1)
    const leftY = Math.min(originalInTangentPxY / height, 0.5)
    const leftYRatio = leftY / LEFT_Y_BASE

    return {
      startY: 0.071 * scaleY,
      c1y: -0.045 * scaleY,
      c2x: -0.009 * scaleX,
      c2y: -0.071 * scaleY,
      endX: -0.02 * scaleX,
      endY: -0.071 * scaleY,
      leftX: 0.02 * scaleX,
      leftY,
      c3x: 0.009 * scaleX,
      c3y: 0.059 * leftYRatio,
      c4y: 0.084 * leftYRatio,
      finalY: 0.116 * leftYRatio,
      down: 1 - 0.116 * leftYRatio,
    }
  }

  /**
   * @param {number} width
   * @param {number} height
   * @param {boolean} flipY - blades--down used to rely on scaleY(-1)
   */
  function buildCssPathForSize(width, height, flipY) {
    const p = computePathParams(width, height)
    if (!p) return null

    const ax = (v) => v * width
    const ay = (v) => (flipY ? 1 - v : v) * height
    const ry = (v) => (flipY ? -v : v) * height

    return [
      `M${ax(1)},${ay(p.startY)}`,
      `c0,${ry(p.c1y)},${ax(p.c2x)},${ry(p.c2y)},${ax(p.endX)},${ry(p.endY)}`,
      `L${ax(p.leftX)},${ay(p.leftY)}`,
      `C${ax(p.c3x)},${ay(p.c3y)},0,${ay(p.c4y)},0,${ay(p.finalY)}`,
      `l0,${ry(p.down)}`,
      `h${width}`,
      `V${ay(p.startY)}`,
    ].join(' ')
  }

  function updateClipPathForElement(blade) {
    const width = blade.offsetWidth
    const height = blade.offsetHeight
    const flipY = blade.classList.contains('blades--down')
    const d = buildCssPathForSize(width, height, flipY)
    if (d) {
      blade.style.clipPath = `path('${d}')`
    }
  }

  function updateAllClipPaths() {
    document.querySelectorAll('.blades').forEach((blade) => updateClipPathForElement(blade))
  }

  function init() {
    setTimeout(updateAllClipPaths, 0)
  }

  let resizeTimeout
  const handleResize = () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(updateAllClipPaths, 100)
  }

  window.addEventListener('resize', handleResize)

  const run = () => {
    init()
    if (window.ResizeObserver) {
      document
        .querySelectorAll('.blades')
        .forEach((blade) => new ResizeObserver(handleResize).observe(blade))
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run)
  } else {
    run()
  }
}

export default bladesDynamicClipPath
